/**
 * FileBrowser Component
 * 
 * Zeigt die Dateien eines Version 2.0 Projekts in einer Baumansicht.
 * Ähnlich wie TaskArea im linken Panel integriert.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFile, 
  faFolder, 
  faFolderOpen,
  faPlus,
  faTrash,
  faDownload,
  faUpload,
  faEdit,
  faFileCode,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';
import { vfs, VFSEntry, FileDefinition } from '../../features/filesystem';
import './FileBrowser.css';

// Datei-Icons basierend auf Sprache/Extension
const getFileIcon = (filename: string, language?: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  
  if (language === 'html' || ext === 'html' || ext === 'htm') {
    return <span className="file-icon html">{'</>'}</span>;
  }
  if (language === 'css' || ext === 'css') {
    return <span className="file-icon css">#</span>;
  }
  if (language === 'javascript' || ext === 'js' || ext === 'data') {
    return <span className="file-icon js">JS</span>;
  }
  if (language === 'typescript' || ext === 'ts') {
    return <span className="file-icon ts">TS</span>;
  }
  if (language === 'json' || ext === 'json') {
    return <span className="file-icon json">{'{}'}</span>;
  }
  
  return <FontAwesomeIcon icon={faFileCode} className="file-icon default" />;
};

interface FileBrowserProps {
  projectId: string;
  files: FileDefinition[];
  activeFile?: string;
  onFileSelect: (file: FileDefinition) => void;
  onFileCreate?: (filename: string) => void;
  onFileDelete?: (file: FileDefinition) => void;
  onFileUpload?: (file: File) => void;
  onProjectDownload?: () => void;
  readOnly?: boolean;
}

export const FileBrowser: React.FC<FileBrowserProps> = ({
  projectId,
  files,
  activeFile,
  onFileSelect,
  onFileCreate,
  onFileDelete,
  onFileUpload,
  onProjectDownload,
  readOnly = false
}) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['/']));
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; file?: FileDefinition } | null>(null);
  const [newFileName, setNewFileName] = useState('');
  const [showNewFileInput, setShowNewFileInput] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Schließe Kontextmenü bei Klick außerhalb
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Gruppiere Dateien nach Verzeichnissen
  const groupedFiles = React.useMemo(() => {
    const groups: Map<string, FileDefinition[]> = new Map();
    groups.set('/', []); // Root-Verzeichnis
    
    for (const file of files) {
      const parts = file.path.split('/');
      if (parts.length === 1) {
        // Datei im Root
        const rootFiles = groups.get('/') || [];
        rootFiles.push(file);
        groups.set('/', rootFiles);
      } else {
        // Datei in Unterverzeichnis
        const dir = parts.slice(0, -1).join('/');
        if (!groups.has(dir)) {
          groups.set(dir, []);
        }
        const dirFiles = groups.get(dir) || [];
        dirFiles.push(file);
        groups.set(dir, dirFiles);
      }
    }
    
    return groups;
  }, [files]);

  const handleFileClick = (file: FileDefinition) => {
    onFileSelect(file);
  };

  const handleContextMenu = (e: React.MouseEvent, file?: FileDefinition) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation(); // Verhindert Bubbling zum Parent
    setContextMenu({ x: e.clientX, y: e.clientY, file });
  };

  const handleNewFile = () => {
    if (!newFileName.trim()) return;
    
    onFileCreate?.(newFileName.trim());
    setNewFileName('');
    setShowNewFileInput(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (readOnly) return;
    e.preventDefault();
    setDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    droppedFiles.forEach(file => {
      onFileUpload?.(file);
    });
  };

  const handleUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        Array.from(files).forEach(file => onFileUpload?.(file));
      }
    };
    input.click();
  };

  return (
    <div 
      className={`file-browser ${dragOver ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onContextMenu={(e) => handleContextMenu(e)}
    >
      {/* Toolbar */}
      {!readOnly && (
        <div className="file-browser-toolbar">
          <button 
            className="toolbar-btn" 
            onClick={() => setShowNewFileInput(true)}
            title="Neue Datei"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          <button 
            className="toolbar-btn" 
            onClick={handleUploadClick}
            title="Datei hochladen"
          >
            <FontAwesomeIcon icon={faUpload} />
          </button>
          {onProjectDownload && (
            <button 
              className="toolbar-btn" 
              onClick={onProjectDownload}
              title="Projekt als ZIP herunterladen"
            >
              <FontAwesomeIcon icon={faDownload} />
            </button>
          )}
        </div>
      )}

      {/* Neue Datei Input */}
      {showNewFileInput && (
        <div className="new-file-input">
          <input
            type="text"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleNewFile();
              if (e.key === 'Escape') setShowNewFileInput(false);
            }}
            placeholder="dateiname.js"
            autoFocus
          />
          <button onClick={handleNewFile}>OK</button>
          <button onClick={() => setShowNewFileInput(false)}>×</button>
        </div>
      )}

      {/* Dateiliste */}
      <div className="file-list">
        {files.map((file) => (
          <div
            key={file.path}
            className={`file-item ${activeFile === file.path ? 'active' : ''}`}
            onDoubleClick={() => handleFileClick(file)}
            onContextMenu={(e) => handleContextMenu(e, file)}
            title="Doppelklick zum Öffnen"
          >
            {getFileIcon(file.path, file.language)}
            <span className="file-name">{file.name || file.path}</span>
            {file.readOnly && <span className="read-only-badge">🔒</span>}
          </div>
        ))}
      </div>

      {/* Drag & Drop Hinweis */}
      {dragOver && (
        <div className="drop-overlay">
          <FontAwesomeIcon icon={faUpload} size="2x" />
          <span>Dateien hier ablegen</span>
        </div>
      )}

      {/* Kontextmenü */}
      {contextMenu && (
        <div 
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {contextMenu.file && (
            <>
              <button onClick={() => {
                if (contextMenu.file) onFileDelete?.(contextMenu.file);
                setContextMenu(null);
              }}>
                <FontAwesomeIcon icon={faTrash} /> Löschen
              </button>
            </>
          )}
          {!contextMenu.file && (
            <>
              <button onClick={() => {
                setShowNewFileInput(true);
                setContextMenu(null);
              }}>
                <FontAwesomeIcon icon={faPlus} /> Neue Datei
              </button>
              <button onClick={() => {
                handleUploadClick();
                setContextMenu(null);
              }}>
                <FontAwesomeIcon icon={faUpload} /> Hochladen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FileBrowser;

